import React, { useEffect, useMemo, useState } from 'react';
import { ArchiveIcon, BoxesIcon, CheckIcon, CircleDollarSignIcon, ClipboardListIcon, PackageCheckIcon, TrendingUpIcon } from 'lucide-react';
import { reconcileSubmission } from '../utils/submissionOutcome';
import { useNisoloProducts } from '../hooks/useNisoloProducts';
import { useNisoloSubmissions } from '../hooks/useNisoloSubmissions';
import { useNisoloOrders } from '../hooks/useNisoloOrders';
import { UploadAIInsights } from './UploadAIInsights';
type SummaryMetric = {
  label: string;
  value: string;
  detail: string;
};
type Batch = {
  arrived: string;
  name: string;
  received: number;
  msrp?: number;
  estimatedRecovery?: number;
  lineItems?: number;
};
const onePeopleSummaryMetrics: SummaryMetric[] = [{
  label: 'Received products',
  value: '189',
  detail: 'Products'
}, {
  label: 'Available for sale products',
  value: '83',
  detail: 'Products'
}, {
  label: 'Live products',
  value: '80',
  detail: 'Products'
}, {
  label: 'Received units',
  value: '1,902',
  detail: 'Units'
}, {
  label: 'Available for sale units',
  value: '1,024',
  detail: 'Units'
}, {
  label: 'Live units',
  value: '1,019',
  detail: 'Units'
}];
const emptySummaryMetrics: SummaryMetric[] = [{
  label: 'Received products',
  value: '0',
  detail: 'Products'
}, {
  label: 'Available for sale products',
  value: '0',
  detail: 'Products'
}, {
  label: 'Live products',
  value: '0',
  detail: 'Products'
}, {
  label: 'Received units',
  value: '0',
  detail: 'Units'
}, {
  label: 'Available for sale units',
  value: '0',
  detail: 'Units'
}, {
  label: 'Live units',
  value: '0',
  detail: 'Units'
}];
const onePeopleIncomingBatches = [{
  arrived: 'Feb 5, 2026',
  name: 'Spring overstock',
  expected: 1365,
  received: 1267,
  progress: 92.8
}, {
  arrived: 'Dec 6, 2025',
  name: 'Assembly part 4',
  expected: 7,
  received: 7,
  progress: 100
}, {
  arrived: 'Aug 30, 2025',
  name: 'August returns',
  expected: 337,
  received: 335,
  progress: 99.4
}, {
  arrived: 'Jul 10, 2025',
  name: 'July overstock',
  expected: 16,
  received: 16,
  progress: 100
}, {
  arrived: 'May 2, 2025',
  name: 'May overstock',
  expected: 258,
  received: 258,
  progress: 100
}];
const eddySummaryMetrics: SummaryMetric[] = [{
  label: 'Received products',
  value: '320',
  detail: 'Products'
}, {
  label: 'Available for sale products',
  value: '86',
  detail: 'Products'
}, {
  label: 'Live products',
  value: '82',
  detail: 'Products'
}, {
  label: 'Received units',
  value: '2,981',
  detail: 'Units'
}, {
  label: 'Available for sale units',
  value: '682',
  detail: 'Units'
}, {
  label: 'Live units',
  value: '669',
  detail: 'Units'
}];
const eddyIncomingBatches = [{
  arrived: 'Nov 4, 2026',
  name: 'Eddy — January 3PL transfer',
  expected: 85,
  received: 86,
  progress: 100
}, {
  arrived: 'Jan 28, 2026',
  name: 'Eddy Samples',
  expected: 0,
  received: 0,
  progress: 0
}, {
  arrived: 'Dec 16, 2025',
  name: 'Eddy — December 2025 3PL transfer',
  expected: 1103,
  received: 1105,
  progress: 100
}, {
  arrived: 'Nov 21, 2025',
  name: 'Eddy 3PL transfer',
  expected: 118,
  received: 120,
  progress: 100
}, {
  arrived: 'Oct 10, 2025',
  name: 'Eddy 3PL Transfer',
  expected: 87,
  received: 86,
  progress: 100
}, {
  arrived: 'May 22, 2025',
  name: 'Eddy — TJX Overflow',
  expected: 0,
  received: 223,
  progress: 97.4
}, {
  arrived: 'May 19, 2025',
  name: 'Eddy — 3PL Transfer',
  expected: 0,
  received: 845,
  progress: 99.7
}, {
  arrived: 'Sep 30, 2024',
  name: 'Eddy — October 3PL Transfer',
  expected: 22,
  received: 25,
  progress: 100
}, {
  arrived: 'Aug 13, 2024',
  name: 'Eddy — Fall 2024',
  expected: 168,
  received: 169,
  progress: 100
}, {
  arrived: 'Apr 8, 2024',
  name: 'Eddy April 2024',
  expected: 0,
  received: 0,
  progress: 100
}, {
  arrived: 'Jan 18, 2024',
  name: 'Eddy — Jan 2024',
  expected: 5,
  received: 0,
  progress: 100
}, {
  arrived: 'Dec 20, 1905',
  name: 'Eddy — 3PL transfer old',
  expected: 0,
  received: 183,
  progress: 100
}, {
  arrived: '—',
  name: 'Eddy — June TRX',
  expected: 139,
  received: 139,
  progress: 98.1
}, {
  arrived: '—',
  name: 'Eddy — 3PL Transfer',
  expected: 0,
  received: 0,
  progress: 0
}];
const currency = (value: number) => value.toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});
export function InventoryDashboard({
  selectedOwner
}: {
  selectedOwner: string;
}) {
  const hasOnePeopleData = selectedOwner === '1 People';
  const hasEddyData = selectedOwner === 'Eddy';
  const isNisolo = selectedOwner === 'Nisolo';
  const {
    inventory: nisolo
  } = useNisoloProducts(isNisolo);
  const {
    data: nisoloSubmissions,
    loading: submissionsLoading,
    error: submissionsError
  } = useNisoloSubmissions(isNisolo);
  const {
    data: nisoloOrders
  } = useNisoloOrders(isNisolo);
  const incomingBatches: Batch[] = isNisolo ? nisoloSubmissions?.submissions ?? [] : hasOnePeopleData ? onePeopleIncomingBatches : hasEddyData ? eddyIncomingBatches : [];
  const [deselected, setDeselected] = useState<string[]>([]);
  useEffect(() => setDeselected([]), [selectedOwner]);
  const isSelected = (batch: Batch) => !deselected.includes(batch.name);
  const toggleBatch = (batch: Batch) => setDeselected(current => current.includes(batch.name) ? current.filter(name => name !== batch.name) : [...current, batch.name]);
  const allSelected = deselected.length === 0;
  const toggleAll = () => setDeselected(allSelected ? incomingBatches.map(batch => batch.name) : []);
  const selectedBatches = incomingBatches.filter(isSelected);
  const batchUnitTotal = incomingBatches.reduce((sum, batch) => sum + batch.received, 0);
  const selectedUnits = selectedBatches.reduce((sum, batch) => sum + batch.received, 0);
  const share = incomingBatches.length === 0 ? 1 : batchUnitTotal > 0 ? selectedUnits / batchUnitTotal : selectedBatches.length / incomingBatches.length;
  const base = isNisolo ? {
    receivedProducts: nisolo?.totalProducts ?? 0,
    availableProducts: nisolo?.products.filter(product => product.units > 0).length ?? 0,
    liveProducts: nisolo?.liveProducts ?? 0,
    receivedUnits: nisoloSubmissions?.totalReceived ?? 0,
    availableUnits: nisolo?.totalUnits ?? 0,
    liveUnits: nisolo?.liveUnits ?? 0,
    totalMsrp: nisoloSubmissions?.totalMsrp ?? 0,
    maxOpportunity: nisoloSubmissions?.totalEstimatedRecovery ?? 0,
    receivedValue: nisoloSubmissions?.totalMsrp ?? 0,
    soldUnits: nisoloOrders?.units ?? 0,
    totalSales: nisoloOrders?.sales ?? 0
  } : hasOnePeopleData ? {
    receivedProducts: 189,
    availableProducts: 83,
    liveProducts: 80,
    receivedUnits: 1902,
    availableUnits: 1024,
    liveUnits: 1019,
    totalMsrp: 130993,
    maxOpportunity: 65437,
    receivedValue: 130993,
    soldUnits: 2920,
    totalSales: 49411
  } : hasEddyData ? {
    receivedProducts: 320,
    availableProducts: 86,
    liveProducts: 82,
    receivedUnits: 2981,
    availableUnits: 682,
    liveUnits: 669,
    totalMsrp: 321656,
    maxOpportunity: 160828,
    receivedValue: 320582,
    soldUnits: 4010,
    totalSales: 325875
  } : {
    receivedProducts: 0,
    availableProducts: 0,
    liveProducts: 0,
    receivedUnits: 0,
    availableUnits: 0,
    liveUnits: 0,
    totalMsrp: 0,
    maxOpportunity: 0,
    receivedValue: 0,
    soldUnits: 0,
    totalSales: 0
  };
  const scaled = (value: number) => Math.round(value * share);
  const selectedLines = selectedBatches.reduce((sum, batch) => sum + (batch.lineItems ?? 0), 0);
  const receivedProducts = selectedLines > 0 ? selectedLines : scaled(base.receivedProducts);
  const receivedUnits = incomingBatches.length > 0 ? selectedUnits : base.receivedUnits;
  const predictedRecovery = isNisolo ? selectedBatches.reduce((sum, batch) => sum + (batch.estimatedRecovery ?? 0), 0) : scaled(base.maxOpportunity);

  // For Nisolo, sales are attributed to the selected submissions by barcode so
  // the summary always reconciles with each upload's report.
  const nisoloMatched = useMemo(() => {
    if (!isNisolo || !nisoloSubmissions) return {
      units: 0,
      sales: 0
    };
    return nisoloSubmissions.submissions.filter(submission => !deselected.includes(submission.name)).reduce((totals, submission) => {
      const outcome = reconcileSubmission(submission, nisoloOrders);
      return {
        units: totals.units + outcome.soldUnits,
        sales: totals.sales + outcome.actualSales
      };
    }, {
      units: 0,
      sales: 0
    });
  }, [isNisolo, nisoloSubmissions, nisoloOrders, deselected]);
  const soldUnits = isNisolo ? nisoloMatched.units : scaled(base.soldUnits);
  const totalSales = isNisolo ? nisoloMatched.sales : scaled(base.totalSales);
  const perUnit = (value: number, units: number) => units > 0 ? (value / units).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2
  }) : '—';
  const summaryMetrics: SummaryMetric[] = [{
    label: 'Unit count',
    value: receivedUnits.toLocaleString('en-US'),
    detail: `Across ${receivedProducts.toLocaleString('en-US')} ${selectedLines > 0 ? 'variant lines' : 'products'}`
  }, {
    label: 'Predicted recovery',
    value: currency(predictedRecovery),
    detail: 'Carli estimate'
  }, {
    label: 'Predicted recovery / unit',
    value: perUnit(predictedRecovery, receivedUnits),
    detail: 'Per unit received'
  }, {
    label: 'Units sold',
    value: soldUnits.toLocaleString('en-US'),
    detail: isNisolo ? 'Matched to selected uploads' : 'All recovery channels'
  }, {
    label: 'Total sales',
    value: currency(totalSales),
    detail: 'Gross recovery revenue'
  }, {
    label: 'Actual recovery / unit',
    value: perUnit(totalSales, soldUnits),
    detail: soldUnits > 0 ? 'Per unit sold' : 'No sales yet'
  }];
  const selectedMsrp = selectedBatches.reduce((sum, batch) => sum + (batch.msrp ?? 0), 0);
  const msrpValue = selectedMsrp > 0 ? selectedMsrp : scaled(base.totalMsrp);
  const totalMsrp = currency(msrpValue);
  const maxOpportunity = currency(predictedRecovery);
  const totalReceivedValue = currency(msrpValue);
  const overstockDetail = `${receivedUnits.toLocaleString('en-US')} units across ${receivedProducts.toLocaleString('en-US')} ${selectedLines > 0 ? 'variant lines' : 'products'}`;
  return <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8 lg:py-14" aria-labelledby="inventory-heading" data-id="element-919" data-mp="7mhuwufjwlgp">
      <section className="flex flex-col gap-5 border-b border-ink/10 pb-8 sm:flex-row sm:items-end sm:justify-between" data-id="element-920" data-mp="15zp0k1ke53x4">
        <div data-id="element-921" data-mp="x5kctr0k08lt">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#BB5E40]" data-id="element-922" data-mp="x5u2tellmfri">
            <ArchiveIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-923" data-mp="1152wkjgn2dn2" />
            Inventory
          </span>
          <h1 id="inventory-heading" className="mt-3 font-display text-4xl font-medium tracking-tight sm:text-5xl" data-id="element-924" data-mp="n5jp7svnj6bt">
            Inventory dashboard
          </h1>
          <p className="mt-3 max-w-2xl text-base leading-relaxed text-ink/65 sm:text-lg" data-id="element-925" data-mp="e6yc4d8y8jif">
            A clear view of inventory received, prepared for sale, and currently live across your approved recovery channels.
          </p>
        </div>
        <span className="inline-flex w-fit items-center gap-2 rounded-full bg-forest/8 px-3 py-1.5 text-xs font-semibold text-forest" data-id="element-926" data-mp="28kzbcz8ev8j1">
          <span className="h-1.5 w-1.5 rounded-full bg-forest" data-id="element-927" data-mp="y65pk25s4fl" />
          {selectedOwner || 'All brands'}
        </span>
      </section>

      <section className="mt-8 grid gap-5 lg:grid-cols-[1.25fr_0.75fr]" aria-label="Inventory overview" data-id="element-928" data-mp="3t3jh05fqsnlo">
        <article className="rounded-3xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-7" aria-labelledby="summary-heading" data-id="element-929" data-mp="apts36juyva7">
          <div className="flex items-center gap-2" data-id="element-930" data-mp="5vfvx1xdgk4h">
            <BoxesIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-931" data-mp="kldmfoirxlwu" />
            <h2 id="summary-heading" className="text-lg font-semibold" data-id="element-932" data-mp="fnv9zo5jsok3">Summary</h2>
          </div>
          <div className="mt-6 grid gap-x-5 gap-y-6 sm:grid-cols-3" data-id="element-933" data-mp="tnmwkda4rd9p">
            {summaryMetrics.map((metric, index) => <div key={metric.label} className={`border-[#BB5E40]/25 pl-4 ${index % 3 === 0 ? 'border-l-0 pl-0' : 'border-l-2'} ${index >= 3 ? 'border-t pt-5 sm:border-t-0 sm:pt-0' : ''}`} data-id="element-934" data-mp="op355xcrp99y" data-mpi={index}>
                <p className="text-sm text-ink/55" data-id="element-935" data-mp="2qggc0ryapr8n">{metric.label}</p>
                <p className="mt-1 font-display text-3xl font-semibold tracking-tight text-ink" data-id="element-936" data-mp="uciac1d0q3bq">{metric.value}</p>
                <p className="mt-1 text-xs text-ink/50" data-id="element-937" data-mp="zatlcff1ujvj">{metric.detail}</p>
              </div>)}
          </div>
        </article>

        <article className="rounded-3xl border border-ink/10 bg-forest p-5 text-cream shadow-sm sm:p-7" aria-labelledby="opportunity-heading" data-id="element-938" data-mp="148v5z4s4ir94">
          <div className="flex items-center gap-2 text-cream/80" data-id="element-939" data-mp="1dbe2z898a9ng">
            <CircleDollarSignIcon className="h-4 w-4" aria-hidden="true" data-id="element-940" data-mp="1d90bf9drbfkn" />
            <h2 id="opportunity-heading" className="text-lg font-semibold text-cream" data-id="element-941" data-mp="1i8wdv46t2yy3">Recovery value</h2>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-5" data-id="element-942" data-mp="1n1phfoqjg18l">
            <div data-id="element-943" data-mp="1s0aa933gnin0">
              <p className="text-xs font-semibold uppercase tracking-wider text-cream/60" data-id="element-944" data-mp="1rypgvgsvu5qq">Total MSRP</p>
              <p className="mt-2 font-display text-3xl font-semibold tracking-tight" data-id="element-945" data-mp="1w36xx87uy4xt">{totalMsrp}</p>
            </div>
            <div data-id="element-946" data-mp="210yrs7s2c14n">
              <p className="text-xs font-semibold uppercase tracking-wider text-cream/60" data-id="element-947" data-mp="20ycl0aczxpnh">Max opportunity</p>
              <p className="mt-2 font-display text-3xl font-semibold tracking-tight" data-id="element-948" data-mp="3gjmwxopwo6wh">{maxOpportunity}</p>
            </div>
          </div>
          <p className="mt-5 border-t border-cream/15 pt-4 text-sm leading-relaxed text-cream/70" data-id="element-949" data-mp="3cj2vwa27oj52">Based on inventory that has been received and evaluated for recovery.</p>
        </article>
      </section>

      <section className="mt-5 grid gap-5 lg:grid-cols-[0.78fr_1.22fr]" data-id="element-950" data-mp="82jnt07hqfg6">
        <article className="rounded-2xl border border-ink/10 bg-paper p-5 sm:p-6" aria-labelledby="overstock-heading" data-id="element-951" data-mp="41q1ucdlzapx">
          <div className="flex items-center gap-2" data-id="element-952" data-mp="3vfvtgiiew657">
            <PackageCheckIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-953" data-mp="3qhu8tt5c7yrm" />
            <h2 id="overstock-heading" className="text-base font-semibold" data-id="element-954" data-mp="3qfq3r7vpctpr">Overstock</h2>
          </div>
          <p className="mt-5 text-sm text-ink/55" data-id="element-955" data-mp="rtnci05bnwpg">Total MSRP value</p>
          <p className="mt-1 font-display text-4xl font-semibold tracking-tight text-ink" data-id="element-956" data-mp="mzj4reo8fknb">{totalReceivedValue}</p>
          <p className="mt-1 text-sm text-ink/55" data-id="element-957" data-mp="6npdouruyb9q">{overstockDetail}</p>
          <div className="mt-6 rounded-xl bg-[#BB5E40]/5 p-4" data-id="element-958" data-mp="blh57nolbvde">
            <div className="flex items-start gap-2" data-id="element-959" data-mp="3tqkdtidrmpik">
              <TrendingUpIcon className="mt-0.5 h-4 w-4 shrink-0 text-[#BB5E40]" aria-hidden="true" data-id="element-960" data-mp="3ts595a7wmchh" />
              <p className="text-sm leading-relaxed text-ink/65" data-id="element-961" data-mp="2k9hu0nm4ey3">Inventory moves into recovery once it is received, checked in, and matched to your current channel and pricing rules.</p>
            </div>
          </div>
        </article>

        <article className="overflow-hidden rounded-2xl border border-ink/10 bg-paper" aria-labelledby="incoming-heading" data-id="element-962" data-mp="gk0r5o36p75r">
          <div className="flex items-center gap-2 border-b border-ink/10 px-5 py-5 sm:px-6" data-id="element-963" data-mp="li29u7wp74gp">
            <ClipboardListIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-964" data-mp="1986cod22b06n" />
            <h2 id="incoming-heading" className="text-base font-semibold" data-id="element-965" data-mp="1dzoet4ftn6t7">Inventory submissions</h2>
          </div>
          <div className="overflow-x-auto" data-id="element-966" data-mp="1nd6nq4gk1aty">
            <table className="w-full min-w-[700px] text-left text-sm" data-id="element-967" data-mp="1nbvlwa6o6d5h">
              <thead className="bg-cream/65 text-xs text-ink/50" data-id="element-968" data-mp="1jblhh1yizusq">
                <tr data-id="element-969" data-mp="1x4b3h6e20zpu">
                  <th scope="col" className="py-3 pl-5 pr-2 font-semibold sm:pl-6" data-id="element-970" data-mp="1x2q8xgrqpgrz">
                    <span className="flex items-center gap-2.5" data-id="element-971" data-mp="1s4exj1lsqy7n">
                      <SelectBox checked={allSelected} onChange={toggleAll} label="Select all submissions" data-id="element-972" data-mp="1s717t902nrtk" />
                      Uploaded
                    </span>
                  </th>
                  <th scope="col" className="px-3 py-3 font-semibold" data-id="element-973" data-mp="10fli3637xoz4">Batch</th>
                  <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-974" data-mp="1eeuxw4niih2k">Unit count</th>
                  <th scope="col" className="px-3 py-3 text-right font-semibold" data-id="element-975" data-mp="19cm79ey43r3m">MSRP value</th>
                  <th scope="col" className="px-5 py-3 text-right font-semibold sm:px-6" data-id="element-976" data-mp="2pqvsi0724zq8">Estimated recovery</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink/10" data-id="element-977" data-mp="2subo0wbt9jzc">
                {incomingBatches.length > 0 ? incomingBatches.map(batch => {
                const checked = isSelected(batch);
                return <tr key={batch.name} className={checked ? '' : 'opacity-45'} data-id="element-978" data-mp="31ugjc83qcrx5">
                      <td className="whitespace-nowrap py-4 pl-5 pr-2 text-ink/60 sm:pl-6" data-id="element-979" data-mp="36ucs0kndh7j1">
                        <span className="flex items-center gap-2.5" data-id="element-980" data-mp="296cfls4pyox3">
                          <SelectBox checked={checked} onChange={() => toggleBatch(batch)} label={`Include ${batch.name} in totals`} data-id="element-981" data-mp="293zpusspza9h" />
                          {batch.arrived}
                        </span>
                      </td>
                      <th scope="row" className="px-3 py-4 text-left font-medium text-ink" data-id="element-982" data-mp="2nvgxf87lqr4o">{batch.name}</th>
                      <td className="px-3 py-4 text-right font-semibold tabular-nums text-ink" data-id="element-983" data-mp="1qgcu1f837ryf">{batch.received.toLocaleString('en-US')}</td>
                      <td className="px-3 py-4 text-right tabular-nums text-ink/70" data-id="element-984" data-mp="1vcths8bdk4az">{batch.msrp && batch.msrp > 0 ? currency(batch.msrp) : '—'}</td>
                      <td className="px-5 py-4 text-right tabular-nums text-ink/70 sm:px-6" data-id="element-985" data-mp="1ivipbu5g06ag">{batch.estimatedRecovery && batch.estimatedRecovery > 0 ? currency(batch.estimatedRecovery) : '—'}</td>
                    </tr>;
              }) : <tr data-id="element-986" data-mp="18zyvreecpjov">
                    <td colSpan={5} className="px-5 py-10 text-center text-sm text-ink/55 sm:px-6" data-id="element-987" data-mp="14wansult4m33">Select an owner to view inventory submissions.</td>
                  </tr>}
              </tbody>
            </table>
          </div>
        </article>
      </section>

      {isNisolo && <section className="mt-5" aria-label="AI insights" data-id="element-988" data-mp="q7ekfaup3x8t">
          <UploadAIInsights uploads={(nisoloSubmissions?.submissions ?? []).filter(submission => !deselected.includes(submission.name))} orders={nisoloOrders} loading={submissionsLoading} error={submissionsError} data-id="element-989" data-mp="q8xw0o5epk49" />
        </section>}
    </div>;
}
function SelectBox({
  checked,
  onChange,
  label
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return <button type="button" role="checkbox" aria-checked={checked} aria-label={label} onClick={onChange} className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors ${checked ? 'border-[#BB5E40] bg-[#BB5E40] text-cream' : 'border-ink/25 bg-paper hover:border-[#BB5E40]/50'}`} data-id="element-990" data-mp="1v58e654m8ki6">
      {checked && <CheckIcon className="h-3 w-3" strokeWidth={3} aria-hidden="true" data-id="element-991" data-mp="1r4xy1zn40dpl" />}
    </button>;
}